import classcat from "classcat"
import React from "react"
import { graphql, useFragment } from "react-relay"

import commonStyles from "../ProjectBudgetForm.module.css"
import styles from "./PhaseEditor.module.css"

import { PhaseEditor_project$key } from "~/forms/ProjectBudgetForm/Phase/__generated__/PhaseEditor_project.graphql"

import currency, { formatCurrency } from "~/helpers/CurrencyHelper"

import PhaseIcon from "~/common/PhaseIcon"

import ExpenseBudgetInput from "../ExpenseBudgetInput"
import Column from "../TableColumn"
import TableInfo from "../TableInfo"
import Row from "../TableRow"

type Props = {
  project: PhaseEditor_project$key
  phaseBudgets: Array<{ phaseId: number; revenueBudget: number }>
  noPhaseBudget: number
  canViewFinancials: boolean
  onPhaseUpdate: (phaseId: number, revenueBudget: number) => void
  onNoPhaseUpdate: (budget: number) => void
}

const PhaseEditor: React.FC<Props> = (props) => {
  const {
    canViewFinancials,
    onPhaseUpdate,
    onNoPhaseUpdate,
    phaseBudgets,
    noPhaseBudget,
  } = props

  const totalRevenueBudget = phaseBudgets.reduce(
    (acc, phase) => acc + phase.revenueBudget,
    noPhaseBudget,
  )

  const project = useFragment<PhaseEditor_project$key>(
    graphql`
      fragment PhaseEditor_project on projects {
        id
        no_phase_revenue_budget: no_phase_revenue_budget_private
        phases {
          id
          name
          color
          revenue_budget: revenue_budget_private
        }
      }
    `,
    props.project,
  )

  const noPhaseItem = {
    id: null,
    name: "No Phase",
    color: "var(--no-phase)",
    revenue_budget: noPhaseBudget || 0,
  }

  return (
    <div className={commonStyles.phaseEditorWrapper} data-test="phase-editor">
      <Row heading>
        <Column>Phase</Column>
        {canViewFinancials && <Column textAlign="right">Phase Budget</Column>}
      </Row>

      <TableInfo className={styles.tableBody}>
        {project.phases.map((phase) => {
          const phaseBudget = phaseBudgets.find(
            (budget) => budget.phaseId === phase.id,
          )
          const value = phaseBudget?.revenueBudget || phase.revenue_budget
          return (
            <Row key={phase.id}>
              <Column className={styles.phaseName}>
                <PhaseIcon size={20} color={phase.color} />
                {phase.name}
              </Column>
              {canViewFinancials && (
                <Column>
                  <ExpenseBudgetInput
                    name={`phase-budget-${phase.name}`}
                    currency={currency}
                    onUpdate={(v) => onPhaseUpdate(phase.id, v)}
                    value={value || 0}
                    maxWidth={110}
                  />
                </Column>
              )}
            </Row>
          )
        })}
        <Row>
          <Column className={styles.phaseName}>
            <PhaseIcon size={20} color={noPhaseItem.color} />
            {noPhaseItem.name}
          </Column>
          {canViewFinancials && (
            <Column>
              <ExpenseBudgetInput
                name="phase-budget-no-phase"
                currency={currency}
                onUpdate={onNoPhaseUpdate}
                value={noPhaseItem.revenue_budget}
                maxWidth={110}
              />
            </Column>
          )}
        </Row>
      </TableInfo>
      {canViewFinancials && (
        <TableInfo
          sticky
          className={classcat([
            commonStyles.totalsWrapper,
            styles.totalsWrapper,
          ])}
          offset={0}
        >
          <div>Total Phase Budget</div>
          <div data-test="total-phase-budget">
            {formatCurrency(totalRevenueBudget)}
          </div>
        </TableInfo>
      )}
    </div>
  )
}

export default PhaseEditor
